export type McpServerStatus =
  | 'active'
  | 'error'
  | 'provisioning'
  | 'disabled';

/**
 * Derive the install's effective status from whatever signals the backend
 * returns. Newer responses omit `status` and rely on the frontend to infer
 * from `enabled` / `lastSyncedAt` / `lastError`. Order matters: explicit
 * status wins, then errors surface immediately, then a successful sync
 * counts as active, then we fall back to "provisioning".
 */
export function deriveMcpStatus(s: {
  status?: McpServerStatus;
  enabled?: boolean;
  lastSyncedAt?: string;
  lastError?: string;
}): McpServerStatus {
  if (s.status) return s.status;
  if (s.lastError) return 'error';
  if (s.enabled === false) return 'disabled';
  if (s.lastSyncedAt) return 'active';
  return 'provisioning';
}

/**
 * Error codes the create endpoint can return when the server's OAuth surface
 * is incompatible. The form branches on these to render the right inline help.
 *   - `pkce_unsupported`: terminal — the server's auth provider can't be used.
 *   - `client_required` / `dcr_unsupported`: the server doesn't support
 *     Dynamic Client Registration; the admin must supply a manual OAuth client.
 */
export type McpCreateErrorCode =
  | 'pkce_unsupported'
  | 'client_required'
  | 'dcr_unsupported';

export interface ManualClient {
  clientId: string;
  clientSecret: string;
}

export interface McpServer {
  serverId: string;
  spaceId: string;
  name: string;
  serverUrl: string;
  defaultGroup?: string;
  scopes?: string[];
  // NOTE: no `dcrSupported` / `manualClientConfigured` / `manualClient` here.
  // agent-core has never returned them on a read (the OAuth app lives in
  // optr-identity, keyed by serverId), so declaring them made every
  // configured client render as "not configured". Read the app state from
  // the credentials response's `ProviderSummary.oauthApp` instead.
  /**
   * AWS-AgentCore-hosted callback URL. If the admin supplied a manual OAuth
   * client, this must be added as an allowed redirect URI on the OAuth app.
   */
  callbackUrl: string;
  /**
   * `'cimd'` = no OAuth app exists by design — the server's AS accepts our
   * hosted client-identity URL (Client ID Metadata Document). Render a CIMD
   * badge instead of "Not configured".
   */
  oauthClientMode?: 'cimd';
  /** Optional — backend may omit and let the frontend derive from signals. */
  status?: McpServerStatus;
  /** Whether the install is enabled. Used when deriving status. */
  enabled?: boolean;
  /** Tool count synced from the MCP server's latest discovery. */
  toolCount?: number;
  lastError?: string;
  lastSyncedAt?: string;
  createdAt: string;
  updatedAt: string;
  providerId?: string;
  /**
   * Auth surface backing this install. Backend may omit on older OAuth-only
   * installs; treat `undefined` as `'oauth'`.
   */
  authMode?: 'oauth' | 'api_key' | 'oauth_manual';
}

/**
 * Which auth surface backs the install. Defaults to `'oauth'` on the wire
 * (omitting the field is back-compat), so we only send `'api_key'` when the
 * admin actually picked the api-key method.
 */
export type McpAuthMode = 'oauth' | 'api_key' | 'oauth_manual';

/**
 * Which credential tier stores the key for an api-key install. Optional on
 * the wire — the backend defaults to `space` on the space route and `org`
 * on the org route. Use `user` for a per-user personal key.
 */
export type McpCredentialLevel = 'user' | 'space' | 'org';

export interface CreateMcpServerInput {
  name: string;
  serverUrl: string;
  defaultGroup?: string;
  /** OAuth scopes; only sent when `authMode` is `'oauth'` (or omitted). */
  scopes?: string[];
  /** Pre-registered OAuth client. Only sent with `'oauth'`. */
  manualClient?: ManualClient;
  providerId?: string;
  /** Defaults to `'oauth'` server-side when omitted. */
  authMode?: McpAuthMode;
  /** Required when `authMode === 'api_key'`. Keyed by `McpApiKeyHeaderSpec.name`. */
  headers?: Record<string, string>;
  credentialLevel?: McpCredentialLevel;
}

export type UpdateMcpServerInput = Partial<CreateMcpServerInput>;

export interface McpSyncResult {
  imported: number;
  updated: number;
  skipped: number;
  removed: number;
  errors: Array<{ tool?: string; message: string }>;
  /** When true the caller must complete the OAuth consent before sync works. */
  authorizationRequired?: boolean;
  authorizationUrl?: string;
}

export interface McpAuthStatus {
  authorized: boolean;
  authorizationUrl?: string;
  scopes?: string[];
  lastError?: string;
  expiresAt?: string;
}

/**
 * Inline tool-result shape returned when an agent tries to call a space-MCP
 * tool without a valid owner token. The chat surface detects this and renders
 * a "Connect <serverName>" CTA in place of the raw JSON pane.
 */
export interface McpAuthRequiredResult {
  requiresAuth: true;
  serverId: string;
  serverName: string;
  authorizationUrl: string;
  hint?: string;
}

/**
 * Org-level MCP server install. Auto-available to every space in the org; a
 * space-level install of the same vendor takes precedence (resolved server-side).
 *
 * `serviceAccount` describes the org's optional Tier-3 fallback credential —
 * used when a calling user has no personal token for the install.
 */
export interface OrgMcpServer {
  serverId: string;
  orgId: string;
  name: string;
  serverUrl: string;
  defaultGroup?: string;
  scopes?: string[];
  // See McpServer: OAuth-app state comes from the credentials response
  // (`ProviderSummary.oauthApp`), never from the server row.
  callbackUrl: string;
  /** See McpServer.oauthClientMode. */
  oauthClientMode?: 'cimd';
  status?: McpServerStatus;
  enabled?: boolean;
  toolCount?: number;
  lastError?: string;
  lastSyncedAt?: string;
  createdAt: string;
  updatedAt: string;
  providerId?: string;
  /**
   * Tier-3 service-account state for the install. Optional — the backend
   * omits it entirely for installs that don't use a service account (e.g.
   * manual-client OAuth installs where the admin's pre-registered client
   * is used for the per-user flow).
   */
  serviceAccount?: {
    configured: boolean;
    scopes?: string[];
    lastError?: string;
  };
  /**
   * Auth surface backing this install. Backend may omit on older OAuth-only
   * installs; treat `undefined` as `'oauth'`.
   */
  authMode?: 'oauth' | 'api_key' | 'oauth_manual';
}

/** Pasted by an admin via the form/modal. Request body only — never echoed back. */
export interface OrgServiceAccountCredential {
  clientId: string;
  clientSecret: string;
  refreshToken?: string;
  scopes?: string[];
}

export interface CreateOrgMcpServerInput {
  name: string;
  serverUrl: string;
  defaultGroup?: string;
  /** OAuth scopes; only sent when `authMode` is `'oauth'` (or omitted). */
  scopes?: string[];
  /**
   * Pre-registered OAuth client supplied by the admin at create time, for
   * servers whose authorization server doesn't support DCR. Used for the
   * per-user OAuth flow — same shape as the space-level install.
   */
  manualClient?: ManualClient;
  /**
   * Tier-3 fallback credential used when a calling user has no personal
   * token. Optional and separate from `manualClient`: the SA's refresh token
   * makes it an org-only concept. The create endpoint accepts it on initial
   * install; replacements go through the dedicated
   * `service-account-credential` endpoint.
   */
  serviceAccount?: OrgServiceAccountCredential;
  providerId?: string;
  /** Defaults to `'oauth'` server-side when omitted. */
  authMode?: McpAuthMode;
  /** Required when `authMode === 'api_key'`. Keyed by `McpApiKeyHeaderSpec.name`. */
  headers?: Record<string, string>;
  credentialLevel?: McpCredentialLevel;
}

export type UpdateOrgMcpServerInput = Partial<
  Omit<CreateOrgMcpServerInput, 'serviceAccount'>
>;

/**
 * Provider catalog — the curated list of MCP servers users can connect to with
 * one click. Surfaced by `GET /api/v1/mcp-providers` and rendered by the
 * "Add MCP server" picker. Custom servers remain supported via the free-form
 * form when `customServerAllowed` is true.
 */
export interface McpScopeOption {
  value: string;
  description: string;
}

export interface McpScopeSpec {
  recommended: readonly string[];
  available?: readonly McpScopeOption[];
}

export interface McpUrlTemplateVariable {
  name: string;
  label: string;
  required: boolean;
  enum?: readonly { value: string; label: string }[];
  default?: string;
}

export interface McpServerUrlTemplate {
  template: string;
  variables: readonly McpUrlTemplateVariable[];
}

export type McpServerUrl = string | McpServerUrlTemplate;

export interface McpApiKeyHeaderSpec {
  /** Header name the runtime sends with each tool call (e.g. "DD-API-KEY"). */
  name: string;
  /** Human label rendered above the secret input. */
  label: string;
  /** Optional link to the page where the user generates the key. */
  obtainUrl?: string;
}

/**
 * One way a provider can be connected. Catalog returns these in display order
 * — the UI auto-selects the first when there's only one, otherwise lets the
 * admin pick. Each method carries its own setup guide (the entry-level guides
 * are gone in this version of the catalog).
 */
export type McpAuthMethod =
  | {
      kind: 'oauth-dcr';
      scopes: McpScopeSpec;
      setupGuideMarkdown: string;
      quickSetupGuideMarkdown?: string;
    }
  | {
      kind: 'oauth-manual-client';
      oauthAppRegistrationUrl: string;
      scopes: McpScopeSpec;
      setupGuideMarkdown: string;
      quickSetupGuideMarkdown?: string;
    }
  | {
      kind: 'api-key';
      headers: readonly McpApiKeyHeaderSpec[];
      setupGuideMarkdown: string;
      quickSetupGuideMarkdown?: string;
    }
  | {
      /**
       * OAuth refresh-token paste — admin uploads a refresh token they
       * already obtained (typically from a local CLI auth flow) instead of
       * running the browser OAuth dance. Field rendering and submit path
       * are not yet wired in the MCP setup form; the catalog can still
       * advertise the method, and the chooser surfaces it with the
       * setup-guide markdown.
       */
      kind: 'oauth-paste-token';
      setupGuideMarkdown: string;
      quickSetupGuideMarkdown?: string;
    };

export interface McpProviderCatalogEntry {
  providerId: string;
  displayName: string;
  description: string;
  category?: string;
  /**
   * Optional remote logo URL from the backend catalog. NOT rendered by the
   * console — the frontend resolves logos by slug from `public/logos/`
   * (see `ProviderLogo`). Kept on the type for backend-response parity.
   */
  logoUrl?: string;
  documentationUrl?: string;
  serverUrl: McpServerUrl;
  defaultGroup: string;
  /** One or more connection methods, ordered for display. */
  authMethods: readonly McpAuthMethod[];
  status: 'ga' | 'beta' | 'experimental';
}

export interface McpProvidersResponse {
  providers: McpProviderCatalogEntry[];
  ourCallbackUrl: string;
  customServerAllowed: boolean;
}
